import { useState, useEffect, useRef } from 'react';
import { Outlet, NavLink, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { PageHeader } from './PageHeader';
import { BarChart3, Brain, LogOut, Menu, X, Lightbulb, PhoneCall, Users, Activity, User, Briefcase, Settings, Link2, Rocket, Archive, Music, Film, UserPlus, Linkedin, BookOpen, Sparkles, ShieldAlert, Eye, FlaskConical, Sun, Moon, PanelLeftClose, PanelLeft, Building2, KanbanSquare, Filter, PenSquare, Map, CalendarClock } from 'lucide-react';
import { useStore } from '../store';
import { ToastContainer } from './Toast';
import { DialogHost } from './ui/dialog';
import { AppErrorBoundary } from './AppErrorBoundary';
import { UserAvatar } from './UserAvatar';
import { useToast } from '../hooks/useToast';
import { t } from '../i18n/translations';
import { BrandingSettingsModal } from './BrandingSettingsModal';
import CallCockpit from '../pages/CallCockpit';
import DialGate from './skriptbibliothek/DialGate';
import { createSSEClient } from '../services/sseClient';
import type { IgPublishSSEEvent, VaultAnalysisSSEEvent, VaultQueueSSEEvent, VideoSSEEvent, TranslateProgressEvent, TranslateCompleteEvent } from '../types';

export function AppShell() {
  const { user, logout, impersonating, stopImpersonating, loadBranding, applyTheme, fetchModuleConfig, handleIgPublishSSE, handleVaultSSE, handleVideoSSE, handleMusicClassifySSE, handleAutopilotSSE, handlePoolImportSSE, handleTranslateProgress, handleTranslateComplete, handleWeekPlanSSE, handleSEOBatchSSE, setSwScrapeProgress, setSwScrapeRunning, setMixScrapeProgress, setMixScrapeRunning, fetchLeadGenLeads, fetchLeadGenStats, fetchViewConfig, viewPages, fetchViewPages, applyLeadUpdate, applyBulkStatus, applyPresence, applyLock, tmClassifying, setTmClassifyError, sseConnectionLost, sseReconnectFn, sseSetConnectionLost } = useStore();
  const { toasts, addToast, removeToast } = useToast();
  // Eigener Selektor statt der Sammel-Destrukturierung oben: die Shell soll nur dann neu rendern,
  // wenn sich das Anruf-Overlay wirklich öffnet oder schliesst.
  const callViewOpen = useStore((s) => s.callViewOpen);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  // Desktop: Sidebar einklappbar (mobil bleibt es beim Hamburger via sidebarOpen). Persistiert.
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => localStorage.getItem('sidebarCollapsed') === '1');
  const [brandingModalOpen, setBrandingModalOpen] = useState(false);
  const sseClientRef = useRef<ReturnType<typeof createSSEClient> | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  // fraud0-Bereich läuft standardmäßig im Light-Theme (Sidebar + Content weiss); Rest der App bleibt dunkel.
  // Per Weiss/Schwarz-Schalter (Sidebar-Fuss) umschaltbar, Auswahl in localStorage gemerkt.
  // '/anrufen' ist wie '/klassik' eine Tabellen-Ansicht (Klassik Stufe 4, PhoneLeadsTable
  // wiederverwendet) - nicht der Wisch-Stapel, dem dieses Theme/Padding gilt.
  const onFraud0 = location.pathname.startsWith('/app/fraud0-leads')
    && !location.pathname.includes('/klassik') && !location.pathname.includes('/anrufen');
  // Mint-Theme wie fraud0, aber mit normalem Seiten-Scroll (kein Swipe-Layout): LinkedIn Accounts.
  const onMintPage = ['/app/fraud0-linkedin', '/app/linkedin-accounts', '/app/linkedin-tracking'].some((p) => location.pathname.startsWith(p));
  // Neues App-Design (Sales-CRM / Direct-Outreach-Look: Coral-Akzent, im Light-Modus Off-White) für die
  // GANZE App · jede /app-Seite, außer dem fraud0-Wisch-Bereich und den Mint-Seiten, die ihr eigenes
  // (bereits helles) Theme haben. So ziehen alle Sektionen den einheitlichen Sales-CRM-Look.
  const isLeitkraftTheme = location.pathname.startsWith('/app') && !onFraud0 && !onMintPage;
  // Default = Dark (fraud0-Brand ist schwarz/mint). Hell nur, wenn explizit gewählt.
  const [fraud0Dark, setFraud0Dark] = useState(() => localStorage.getItem('fraud0Theme') !== 'light');
  const fraud0Light = (onFraud0 || onMintPage) && !fraud0Dark;
  const toggleFraud0Theme = () => setFraud0Dark((d) => {
    const next = !d;
    localStorage.setItem('fraud0Theme', next ? 'dark' : 'light');
    return next;
  });
  const lang = user?.lang || 'de';
  const isAdmin = user?.role === 'admin';
  // Scoped roles: locked to the Fraud0-Leads workflow only. 'setter' (e.g. Lida) enriches leads and
  // hands them to Business Outreach; 'head_of_sales' only reviews & signs off on the leads page.
  const isSetter = user?.role === 'setter';
  const isHos = user?.role === 'head_of_sales';
  const isFraud0Setter = user?.role === 'fraud0_setter';
  // setter-junior (Marc): darf NUR Freundschaftsanfragen senden (suchen/neu/wartend) · kein Schreiben.
  // Bekommt die fokussierte, sidebar-lose fraud0-Ansicht (wie die Reviewer-Rollen, aber KEIN Reviewer-Flow).
  const isSetterJunior = user?.role === 'setter-junior';
  // fraud0 review roles (HoS + fraud0_setter) + setter-junior: sidebar-less full-screen fraud0-Ansicht.
  const isChromeless = isHos || isFraud0Setter || isSetterJunior;
  const isScoped = isSetter || isHos || isFraud0Setter || isSetterJunior;
  const SCOPED_HOME = '/app/fraud0-leads';
  // Pages each scoped role may open (everything else redirects to SCOPED_HOME).
  // fraud0_setter darf zusätzlich die „fraud0 LinkedIn"-Seite (Sende-Accounts verwalten + Tracking).
  const SCOPED_PATHS = isChromeless
    ? ['/app/fraud0-leads', ...(isFraud0Setter ? ['/app/fraud0-linkedin'] : [])]
    : ['/app/fraud0-leads', '/app/outreach'];

  useEffect(() => {
    loadBranding().then(() => applyTheme());
    fetchModuleConfig();
    fetchViewConfig();
    fetchViewPages();
  }, []);

  // Menü öffnen aus der fraud0-Kopfleiste (dort sitzt ein sauber integrierter Menü-Button statt des
  // floatenden Hamburgers) · via Event, damit die fraud0-Page nicht setSidebarOpen durchgereicht bekommt.
  useEffect(() => {
    const open = () => setSidebarOpen(true);
    window.addEventListener('app-open-sidebar', open);
    return () => window.removeEventListener('app-open-sidebar', open);
  }, []);

  // iOS-Safari: html+body-Hintergrund an das fraud0-Theme koppeln, damit die Seite bis UNTER die
  // Safari-Leisten/Overscroll-Zonen durchläuft (statt am Body-Blau #0B0F19 abzuschneiden). Mit
  // viewport-fit=cover (index.html) reicht der Content dann randlos bis in die Safe-Area-Bereiche.
  useEffect(() => {
    // Browser-Chrome-Hintergrund ans jeweilige Theme koppeln. fraud0/Mint: eigenes Hell/Dunkel. Neues
    // App-Design (isLeitkraftTheme): Off-White im Light-Modus, sonst neutrales Schwarz. Rest: altes Blau.
    const bg = (onFraud0 || onMintPage)
      ? (fraud0Light ? '#F5F5F6' : '#0a0a0a')
      : isLeitkraftTheme
        ? (fraud0Dark ? '#0a0a0a' : '#F5F5F6')
        : '#0B0F19';
    document.documentElement.style.backgroundColor = bg;
    document.body.style.backgroundColor = bg;
    // theme-color tönt die Safari-Leisten (oben/unten) passend → randloser App-Look.
    let meta = document.querySelector('meta[name="theme-color"]');
    if (!meta) {
      meta = document.createElement('meta');
      meta.setAttribute('name', 'theme-color');
      document.head.appendChild(meta);
    }
    meta.setAttribute('content', bg);
    return () => {
      document.documentElement.style.backgroundColor = '';
      document.body.style.backgroundColor = '';
    };
  }, [onFraud0, onMintPage, fraud0Light, isLeitkraftTheme, fraud0Dark]);

  useEffect(() => {
    localStorage.setItem('sidebarCollapsed', sidebarCollapsed ? '1' : '0');
  }, [sidebarCollapsed]);

  useEffect(() => {
    const client = createSSEClient((data) => {
      if (data.event === 'ig_publish_update') {
        const evt = data as unknown as IgPublishSSEEvent;
        handleIgPublishSSE(evt);
        if (evt.status === 'success') {
          addToast(`Published to @${evt.ig_username || 'Instagram'}`, 'success');
        } else if (evt.status === 'error') {
          addToast(`Publish failed: ${evt.error || 'Unknown error'}`, 'error');
        }
      }
      if (data.event === 'vault_analysis_complete') {
        const evt = data as unknown as VaultAnalysisSSEEvent;
        handleVaultSSE(evt);
        if (evt.status === 'succeeded') {
          addToast('Vault analysis complete', 'success');
        } else if (evt.status === 'failed') {
          addToast('Vault analysis failed', 'error');
        }
      }
      /* Der Lead hat seine persoenliche Seite geoeffnet.
         Ueberall sichtbar, nicht nur im CRM: das ist der Moment, in dem ein Anruf sitzt,
         und wer gerade in einer anderen Ansicht steht, wuerde ihn sonst verpassen. Nur beim
         ERSTEN Mal laut — spaetere Aufrufe stehen in der Firmenzeile, aber jedes Neuladen
         seiner Seite als Meldung waere nach einer Stunde nur noch Rauschen. */
      if (data.event === 'fraud_seite_geoeffnet') {
        // Steht das CRM gerade offen, soll die Firmenzeile es sofort zeigen und nicht erst
        // beim naechsten Laden. Ein Fensterereignis statt eines weiteren Store-Slice: es
        // gibt genau einen Zuhoerer, und der lebt in einer lazy geladenen Seite.
        window.dispatchEvent(new CustomEvent('fraud-seite-geoeffnet', { detail: data }));
      }
      if (data.event === 'fraud_seite_geoeffnet' && data.erstes) {
        addToast(`Website von ${data.firma ?? 'einem Lead'} wurde geöffnet`, 'success', {
          // Bleibt stehen, bis jemand hinsieht. Vier Sekunden waeren zu wenig fuer eine
          // Nachricht, auf die man reagieren soll — und wer sie verpasst, erfaehrt es sonst
          // erst zufaellig beim naechsten Blick in die Liste.
          sticky: true,
          action: { label: 'Ansehen', onAction: () => navigate('/app/outreach') },
        });
      }
      if (data.event === 'vault_queue_update') {
        handleVaultSSE(data as unknown as VaultQueueSSEEvent);
      }
      if (data.event === 'video_update') {
        handleVideoSSE(data as unknown as VideoSSEEvent);
        const evt = data as unknown as VideoSSEEvent;
        if (evt.status === 'ready') {
          addToast(`Video #${evt.video_id} ready`, 'success');
        } else if (evt.status === 'error') {
          addToast(`Video #${evt.video_id} failed`, 'error');
        }
      }
      if (data.event === 'music_classify_progress' || data.event === 'music_classify_complete') {
        handleMusicClassifySSE(data);
      }
      if (
        data.event === 'autopilot_notification' ||
        data.event === 'autopilot_skip' ||
        data.event === 'autopilot_phase_upgrade' ||
        data.event === 'autopilot_paused' ||
        data.event === 'autopilot_resumed'
      ) {
        handleAutopilotSSE(data.event, data);
        if (data.event === 'autopilot_notification') {
          const ntype = (data as any).notification_type;
          if (ntype === 'error') {
            addToast((data as any).title || 'Autopilot error', 'error');
          } else if (ntype === 'milestone') {
            addToast((data as any).title || 'Autopilot milestone', 'success');
          }
        }
      }
      if (data.event === 'autopilot_scheduled') {
        handleAutopilotSSE(data.event, data);
        addToast(`Post scheduled for ${new Date((data as any).scheduled_at).toLocaleTimeString()}`, 'success');
      }
      if (data.event === 'autopilot_queue_low') {
        handleAutopilotSSE(data.event, data);
        addToast(`Content queue low: ${(data as any).queue_depth} slot(s) remaining`, 'info');
      }
      // Stat Library health notifications
      if (data.event === 'stat_library_low' || data.event === 'stat_library_critical' ||
          data.event === 'stat_library_refreshed' || data.event === 'stat_library_empty') {
        const d = data as any;
        const level = d.level === 'error' ? 'error' : d.level === 'success' ? 'success' : 'info';
        addToast(d.message || `Stat Library: ${d.niche}`, level);
      }
      // LinkedIn FR soft daily-limit warning (observation · warn, never block)
      if (data.event === 'fr_daily_limit') {
        const d = data as any;
        addToast(
          `${d.name}: heute ${d.today_count} Freundschaftsanfragen gesendet · LinkedIn-Tageslimit (${d.daily_limit}) erreicht. Heute keine weiteren senden.`,
          'error',
          { sticky: true },
        );
      }
      // Lead-Meldebestand: der geprüfte Vorrat des „Neu"-Stapels unterschreitet die eingestellte
      // Schwelle. Geht nur an Admins (serverseitig per user_key adressiert). Kritisch = sticky,
      // weil ein leerer Vorrat still ungeprüfte Leads in den Stapel spült.
      if (data.event === 'lead_stock_low') {
        const d = data as any;
        const level = d.level === 'error' ? 'error' : 'info';
        addToast(d.message, level, { sticky: level === 'error' });
      }
      if (data.event === 'pool_import_complete') {
        handlePoolImportSSE(data as { strategy_id: number; niche: string; inserted: number; skipped: number; pending: number });
      }
      if (data.event === 'translate_progress') {
        const evt = data as unknown as TranslateProgressEvent;
        if (evt.user_id === user?.id) {
          handleTranslateProgress(evt);
        }
      }
      if (data.event === 'translate_complete') {
        handleTranslateComplete(data as unknown as TranslateCompleteEvent);
      }
      if (data.event === 'week_plan_slot') {
        handleWeekPlanSSE(data as { slot_id: number; status: string; generation_id?: number; error?: string });
      }
      if (data.event === 'seo_batch_progress' || data.event === 'seo_batch_complete') {
        handleSEOBatchSSE(data as Record<string, unknown>);
      }
      if (data.event === 'leadgen_similarweb_start' || data.event === 'leadgen_similarweb_progress') {
        const d = data as any;
        setSwScrapeProgress({ done: d.done ?? 0, total: d.total ?? 0 });
        setSwScrapeRunning(true);
      }
      if (data.event === 'leadgen_similarweb_complete') {
        const d = data as any;
        setSwScrapeProgress(null);
        setSwScrapeRunning(false);
        fetchLeadGenLeads();
        fetchLeadGenStats();
        if (d.error) {
          addToast(`SimilarWeb-Scrape fehlgeschlagen: ${d.error}`, 'error');
        } else {
          addToast(`SimilarWeb fertig: ${d.resolved}/${d.processed} Visits, ${d.band_hits} im ICP-Band${d.capped ? ' (Budget-Cap erreicht)' : ''}`, 'success');
        }
      }
      if (data.event === 'leadgen_similarweb_mix_start' || data.event === 'leadgen_similarweb_mix_progress') {
        const d = data as any;
        setMixScrapeProgress({ done: d.done ?? 0, total: d.total ?? 0 });
        setMixScrapeRunning(true);
      }
      if (data.event === 'leadgen_similarweb_mix_complete') {
        const d = data as any;
        setMixScrapeProgress(null);
        setMixScrapeRunning(false);
        fetchLeadGenLeads();
        fetchLeadGenStats();
        if (d.error) {
          addToast(`Marketing-Mix-Scrape fehlgeschlagen: ${d.error}`, 'error');
        } else {
          addToast(`Marketing-Mix fertig: ${d.resolved}/${d.processed} Leads${d.empty ? `, ${d.empty} ohne Daten` : ''}${d.capped ? ' (Budget-Cap erreicht)' : ''}`, 'success');
        }
      }
      if (data.event === 'leadgen_meta_source_complete') {
        const d = data as any;
        fetchLeadGenLeads();
        fetchLeadGenStats();
        if (d.error) {
          addToast(`Meta-Quelle fehlgeschlagen: ${d.error}`, 'error');
        } else {
          addToast(`Meta-Quelle fertig: ${d.inserted ?? 0} neue Domains (${d.sourced ?? 0} frisch)`, 'success');
        }
      }
      if (data.event === 'view_config_updated') {
        // Admin changed a view config: every client refetches its OWN effective config (live).
        // `sichtbar` lives in the same config, so the menu's Arbeitsflächen refresh too.
        fetchViewConfig((data as any).page);
        fetchViewPages();
      }
      if (data.event === 'leadgen_lead_updated') {
        const lead = (data as any).lead;
        if (lead) {
          applyLeadUpdate(lead);  // Tabellenansicht (Zustand-Store)
          // Wisch-Stapel hält eine eigene lokale Queue → per Window-Event live spiegeln (Patch/Drop/Insert).
          // `by` (wer gehandelt hat) muss mit: der Stapel zieht die offene Karte nur bei FREMDEN
          // Änderungen weg, sonst nähme er dem Setter seine eigene Karte mitten im Schritt aus der Hand.
          // `scope` muss mit: bei 'phones' sind NUR Nummern dazugekommen · der Stapel patcht sie dann
          // in die offene Karte, statt die Karte neu gegen den Stapel zu prüfen und sie dem Kollegen
          // dabei wegzuziehen (siehe useLeadQueue.applyExternalUpdate).
          window.dispatchEvent(new CustomEvent('fraud0-lead-updated', {
            detail: { lead, by: (data as any).by, scope: (data as any).scope },
          }));
        }
      }
      if (data.event === 'leadgen_call_activity') {
        // Jemand hat gewählt. Absichtlich NICHT über applyLeadUpdate/`fraud0-lead-updated`: das
        // Signal trägt keinen Lead und darf die Wisch-Queue nicht anfassen (siehe call_dial —
        // Wählen ist hochfrequent, jedes volle Lead-Update wäre dort Lärm). Nur die Telefon-Tabelle
        // im Sales CRM hört zu, deren Reihenfolge sich durch einen Anruf ändert.
        const d = data as any;
        window.dispatchEvent(new CustomEvent('fraud0-call-activity', {
          detail: { lead_id: d.lead_id, by: d.by },
        }));
      }
      if (data.event === 'leadgen_lead_bulk_updated') {
        const d = data as any;
        applyBulkStatus(d.ids || [], d.status);  // Tabellenansicht (Zustand-Store)
        window.dispatchEvent(new CustomEvent('fraud0-lead-bulk-updated', { detail: { ids: d.ids || [], status: d.status } }));
      }
      if (data.event === 'leadgen_comment_added') {
        // Team-Kommentar: die offene Kommentar-Sektion (Detail-Panel) hängt ihn live an.
        const d = data as any;
        if (d.comment) window.dispatchEvent(new CustomEvent('fraud0-comment-added', { detail: { lead_id: d.lead_id, comment: d.comment } }));
      }
      if (data.event === 'leadgen_comment_updated') {
        // Kommentar bearbeitet → live ersetzen.
        const d = data as any;
        if (d.comment) window.dispatchEvent(new CustomEvent('fraud0-comment-updated', { detail: { lead_id: d.lead_id, comment: d.comment } }));
      }
      if (data.event === 'leadgen_comment_deleted') {
        // Kommentar (+ ggf. Antworten) gelöscht → live entfernen.
        const d = data as any;
        window.dispatchEvent(new CustomEvent('fraud0-comment-deleted', { detail: { lead_id: d.lead_id, ids: d.ids || [] } }));
      }
      if (data.event === 'leadgen_reply') {
        // Lautes Positiv-Event fürs ganze Team: ein Lead hat geantwortet · verbindet die
        // Top-of-Funnel-Arbeit (FRs, Pitches) sichtbar mit echten Outcomes.
        const d = data as any;
        addToast(`🎉 ${d.domain ?? 'Ein Lead'} hat geantwortet!`, 'success');
      }
      if (data.event === 'lead_distribution_updated') {
        // Admin hat die Verteilungs-Config gespeichert (z.B. flow_enabled-Toggle): die fraud0-Leads-Seite
        // liest sie nur beim Mount → per Window-Event live nachziehen, statt einen Reload zu verlangen.
        window.dispatchEvent(new CustomEvent('fraud0-distribution-updated', { detail: data }));
      }
      if (data.event === 'sales_script_updated') {
        // Skript geändert (Main-Block, Block-Metadaten, ein neu geteilter Baustein oder eine in der
        // Bibliothek gespeicherte neue Fassung) — der Skript-Stand liegt lokal in den Bauteilen
        // (kein Store-Slice dafür, siehe research zur Fork-Zustand-Duplikation), darum per
        // Window-Event statt Store-Action. Es hört: `ScriptLibrary` (der Block-Editor).
        // Das CallCockpit hört ABSICHTLICH nicht — ein Skript, das sich während des Gesprächs unter
        // dem Finger austauscht, verschiebt die Position mitten im Satz. Es lädt beim Öffnen.
        // `bereich` fährt seit 2026-08-20 mit: dieselbe Bibliothek trägt Telefon, Mail und
        // LinkedIn, und ein offener Telefon-Tab soll nicht neu laden, weil jemand einen
        // Mail-Baustein geändert hat. Wer ihn nicht ausliest, lädt wie bisher.
        const d = data as any;
        window.dispatchEvent(new CustomEvent('sales-script-updated',
          { detail: { niche: d.niche, language: d.language, bereich: d.bereich } }));
      }
      if (data.event === 'leadgen_lock_changed') {
        // Shared-mode soft-lock: relay to the swipe stack (own local queue) so a lead locked by another
        // user drops live, and a released lead re-surfaces. The stack listens for this window event.
        window.dispatchEvent(new CustomEvent('fraud0-lock-changed', { detail: data }));
        // Klassik Stufe 3: derselbe Event füttert zusätzlich `locksByLead` — ABER nur mit einem echten
        // Lock (`lock_until` gesetzt, aus /claim). `state: 'locked'` ohne `lock_until` ist der reine
        // Präsenz-Reflex desselben Broadcasts (`_broadcast_presence_change`, shared mode) und würde sonst
        // die Sperr-Badge bei jedem Präsenz-Wechsel fälschlich zeigen — dafür ist `presenceByLead` da.
        const d = data as any;
        if (d.state === 'locked' && d.lock_until) {
          applyLock(d.lead_id, { active: true, by: d.lock_by ?? null, byName: d.username ?? null });
        } else if (d.state === 'released') {
          applyLock(d.lead_id, { active: false, by: null, byName: null });
        }
      }
      if (data.event === 'leadgen_presence_changed') {
        // Shared-mode live presence: who is currently focused on a lead → the card badges it.
        const d = data as any;
        applyPresence(d.lead_id, d.present || []);
      }
      if (data.event === 'leadgen_reservations_updated') {
        // Daily reservation ran (admin button or auto-job): every client refetches so the
        // "Meine"/"Reserviert"-Badges + the "Meine Leads"-Filter reflect the new owners live.
        const d = data as any;
        fetchLeadGenLeads();
        fetchLeadGenStats();
        const counts = d.counts || {};
        const summary = Object.entries(counts).map(([u, n]) => `${u} ${n}`).join(' · ');
        addToast(`Tagesleads verteilt${summary ? `: ${summary}` : ''}`, 'success');
      }
      if (data.event === 'leadgen_impressum_complete') {
        const d = data as any;
        fetchLeadGenLeads();
        if (d.error) {
          addToast(`Impressum-Extraktion fehlgeschlagen: ${d.error}`, 'error');
        } else {
          addToast(`Impressum fertig: ${d.processed ?? 0} Leads, ${d.with_people ?? 0} mit GF-Namen`, 'success');
        }
      }
    }, {
      onConnectionLost: (reconnectFn) => {
        sseSetConnectionLost(true, reconnectFn);
      },
      onReconnected: () => {
        sseSetConnectionLost(false, null);
        addToast('Reconnected.', 'success');
      },
      onDeactivated: () => {
        addToast('Your account has been deactivated.', 'error');
      },
    });
    client.connect();
    sseClientRef.current = client;
    return () => {
      client.close();
      sseClientRef.current = null;
    };
  }, []);

  // Staleness guard: if tmClassifying is stuck (3 missed heartbeats = 90s), reset it
  useEffect(() => {
    if (!tmClassifying) return;
    const client = sseClientRef.current;
    if (!client) return;
    const unregister = client.onStale(() => {
      setTmClassifyError('Classification timed out: no response from server');
      useStore.setState({ tmClassifying: false, tmClassifyProgress: null });
    }, { heartbeatCount: 3 });
    return () => unregister();
  }, [tmClassifying]);

  // Arbeitsflächen aus der view_configs-Registry (Nr. 74 Stufe 3): SICHTBAR entscheidet der
  // Server (`viewPages`, siehe /api/view-pages), Icon + Reihenfolge bleiben lokal — die Registry
  // muss dafür keine Icon-Namen tragen. Neue Ansichten bekommen hier einfach einen Eintrag;
  // ohne Eintrag fällt das Icon auf FlaskConical zurück statt den Menüpunkt zu verschlucken.
  const ARBEITSFLAECHEN_ICONS: Record<string, typeof FlaskConical> = {
    fraud0_leads: FlaskConical,
    fraud0_leads_anrufen: PhoneCall,
    fraud0_leads_heute_dran: CalendarClock,
  };
  const arbeitsflaechenNavItems = viewPages.map((p) => ({
    to: p.route,
    icon: ARBEITSFLAECHEN_ICONS[p.page] || FlaskConical,
    label: p.titel,
  }));

  const navGroups = [
    {
      items: [
        { to: '/app', icon: BarChart3, label: t('nav.master_control', lang) },
        { to: '/app/aufgaben', icon: KanbanSquare, label: 'Aufgaben' },
        ...(isAdmin ? [{ to: '/app/lp-analytics/funnel', icon: Filter, label: 'Landingpage' }] : []),
      ],
    },
    {
      label: t('nav.group_content_engine', lang),
      items: [
        { to: '/app/ig-parasite', icon: PenSquare, label: 'Content' },
        { to: '/app/linkedin', icon: Linkedin, label: 'LinkedIn' },
        // Der neue Weg als eigene Flaeche. Sparkles statt Linkedin, weil zwei
        // gleiche Symbole direkt untereinander in der Leiste nicht trennbar sind.
        { to: '/app/linkedin-neu', icon: Sparkles, label: 'LinkedIn Neu' },
        { to: '/app/vault', icon: Archive, label: t('nav.vault', lang) },
        { to: '/app/trending-music', icon: Music, label: t('nav.trending_music', lang) },
        { to: '/app/reels', icon: Film, label: t('nav.reels', lang) },
      ],
    },
    {
      label: t('nav.group_strategy', lang),
      items: [
        { to: '/app/expansion', icon: Sparkles, label: lang === 'de' ? 'Expansion' : 'Expansion' },
        { to: '/app/strategy-os', icon: Lightbulb, label: t('nav.strategy_os', lang) },
        // Die Ablauf-Zeichenflaeche · das gleichnamige Kanban-Board heisst seit
        // 12.08. "Aufgaben" und liegt weiter in der obersten Gruppe.
        { to: '/app/roadmap', icon: Map, label: 'Roadmap' },
        { to: '/app/satellite-os', icon: Rocket, label: t('nav.satellite_os', lang) },
      ],
    },
    {
      label: 'Mind OS',
      items: [
        { to: '/app/mind', icon: Brain, label: 'Mind OS' },
      ],
    },
    {
      label: t('nav.group_sales_outreach', lang),
      items: [
        { to: '/app/sales-crm', icon: PhoneCall, label: t('nav.sales_crm', lang) },
        { to: '/app/outreach', icon: Briefcase, label: t('nav.outreach', lang) },
        { to: '/app/contact-pool', icon: UserPlus, label: t('nav.contact_pool', lang) },
        { to: '/app/fraud0-leads', icon: ShieldAlert, label: 'fraud0 Leads' },
        ...arbeitsflaechenNavItems,
        { to: '/app/fraud0-linkedin', icon: Linkedin, label: 'fraud0 LinkedIn' },
      ],
    },
    {
      label: t('nav.group_system', lang),
      items: [
        { to: '/app/activity', icon: Activity, label: t('nav.activity_log', lang) },
        { to: '/app/integrations', icon: Link2, label: t('nav.integrations', lang) },
        ...(isAdmin ? [
          { to: '/app/users', icon: Users, label: t('nav.user_management', lang) },
          { to: '/app/admin/firms', icon: Building2, label: 'Firmen' },
          { to: '/app/admin/views', icon: Eye, label: lang === 'de' ? 'Ansichten' : 'Views' },
          { to: '/app/admin/stat-library', icon: BookOpen, label: 'Stat Library' },
          { to: '/app/pipeline-events', icon: Activity, label: 'Pipeline Events' },
        ] : []),
      ],
    },
  ];

  // Zentraler Seiten-Header: Icon + Titel kommen aus dem passenden Nav-Eintrag (längster Pfad-Match =
  // spezifischste Route). So sitzt der Header app-weit an EINER festen Position (in AppShell gerendert,
  // nicht pro Seite) · die Seiten hängen nur noch ihre Aktionen per <PageActions> in den rechten Slot.
  const currentNav = navGroups
    .flatMap((g) => g.items)
    .filter((i) => location.pathname === i.to || location.pathname.startsWith(i.to + '/'))
    .sort((a, b) => b.to.length - a.to.length)[0];

  // Scoped lockdown: nav shows ONLY the Fraud0-Leads workflow. The setter additionally gets Business
  // Outreach (they take a lead all the way to the first contact); the HoS only reviews/approves leads.
  const visibleNavGroups = isScoped
    ? [{ items: [
        { to: '/app/fraud0-leads', icon: ShieldAlert, label: 'fraud0 Leads' },
        // Nur `setter` bekommt hier je eine Sidebar (die anderen drei scoped Rollen sind
        // `isChromeless` und rendern nie diese Liste) — die Arbeitsflächen aus der Registry
        // gehören trotzdem hierher, sonst sieht `setter` „sichtbar" nur über die URL, nie im Menü.
        ...(isSetter ? arbeitsflaechenNavItems : []),
        ...(isSetter ? [{ to: '/app/outreach', icon: Briefcase, label: t('nav.outreach', lang) }] : []),
      ] }]
    : navGroups;

  // Shared content: impersonation/SSE-lost banners + the scoped-redirect-guarded Outlet. Reused by
  // both the normal (sidebar) shell and the chromeless review-role shell · so live-sync, the
  // impersonation "stop" affordance and toasts work identically without a sidebar.
  const shellBody = (
    <>
      {impersonating && (
        // relative z-[90]: die Leiste ist der Notausgang aus einer fremden Rolle und gehoert
        // ueber JEDEN Vorhang. Ohne z-Index lag sie im normalen Fluss und damit unter dem
        // Pflicht-Modal "So funktioniert's" (fixed inset-0 z-[80], pages/fraud0/Fraud0Leads.tsx),
        // dessen dismiss() ohne gewaehlten LinkedIn-Account abbricht - auch beim Klick auf den
        // Hintergrund. Wer als head_of_sales/setter/setter-junior hineinsah, sass fest.
        // Das Modal selbst bleibt bewusst unnachgiebig: die Kontowahl ist Absicht.
        <div className="relative z-[90] flex items-center justify-between gap-3 px-4 py-2 bg-amber-500/15 border-b border-amber-500/30 text-amber-300 text-sm shrink-0">
          <span className="inline-flex items-center gap-2">
            <Eye size={15} /> Du siehst die App als <strong>@{impersonating.username}</strong> ({impersonating.role})
          </span>
          <button
            onClick={stopImpersonating}
            className="px-3 py-1 rounded-lg bg-amber-500/20 hover:bg-amber-500/30 text-amber-200 text-xs font-medium transition-colors shrink-0"
          >
            Zurück zu Admin
          </button>
        </div>
      )}
      {sseConnectionLost && (
        <div className="flex items-center justify-between gap-3 px-4 py-2 bg-yellow-500/10 border-b border-yellow-500/20 text-yellow-400 text-sm shrink-0">
          <span>{t('sse.connection_lost', lang)}</span>
          <button
            onClick={() => sseReconnectFn?.()}
            className="px-3 py-1 rounded-lg bg-yellow-500/20 hover:bg-yellow-500/30 text-yellow-300 text-xs font-medium transition-colors"
          >
            {t('sse.reconnect', lang)}
          </button>
        </div>
      )}
      {/* fraud0-leads (Swipe-Ansicht, nicht Klassik-Tabelle) ist als "passt exakt ins Fenster" gebaut
          (h-full/flex-1-Kette in Fraud0Leads.tsx/LeadStack.tsx) · NIE selbst scrollen, sonst entsteht
          Doppel-Scroll (Seite UND Karte gleichzeitig, siehe Nutzer-Feedback). Nur die Karte scrollt bei
          Bedarf intern (CardShell). Alle anderen Seiten (Tabellen, Formulare, Outreach) behalten den
          normalen Seiten-Scroll.
          WICHTIG min-h-0: als Flex-Item in main's Spalten-Layout hat dieser Wrapper sonst per CSS-Default
          min-height:auto (= inhaltsbasiert) → er würde NICHT auf die verfügbare Höhe schrumpfen, sondern
          durch seinen Inhalt wachsen. overflow-hidden schneidet dann den Überschuss einfach unten ab, statt
          dass die Karte intern scrollt (genau der "unten abgeschnitten"-Bug). min-h-0 erzwingt das Schrumpfen
          auf die tatsächlich verfügbare Höhe, danach übernimmt CardShells eigenes overflow-y-auto den Rest. */}
      <div className={`flex-1 min-h-0 overscroll-contain ${onFraud0 ? 'overflow-y-auto overflow-x-hidden p-2 sm:p-4' : onMintPage ? 'overflow-y-auto p-2 sm:p-4' : 'overflow-y-auto p-4 lg:p-8'}${(onFraud0 || onMintPage) ? ' fraud0-area bg-bg-primary' : ''}${fraud0Light ? ' fraud0-light' : ''}${
        isLeitkraftTheme
          ? (fraud0Dark ? ' fraud0-area bo-purple bg-bg-primary' : ' fraud0-area fraud0-light bo-purple bg-bg-primary')
          : ''}`}>
        {isScoped && !SCOPED_PATHS.some((p) => location.pathname.startsWith(p))
          ? <Navigate to={SCOPED_HOME} replace />
          : isLeitkraftTheme
            // Einheitliche Seiten-Breite + EIN zentraler Header: der Header (Icon + Titel) wird HIER
            // gerendert, damit er auf JEDER Seite an exakt derselben Position sitzt (nicht mehr pro Seite,
            // was zu unterschiedlichen Höhen führte). Seiten hängen ihre Aktionen per <PageActions> in den
            // rechten Slot (#page-header-actions). Content zentral auf max-w-6xl.
            ? <div className="mx-auto w-full max-w-6xl">
                {currentNav && (
                  <PageHeader icon={currentNav.icon} title={currentNav.label}
                              right={<span id="page-header-actions" className="contents" />} />
                )}
                <AppErrorBoundary resetKey={location.pathname}><Outlet /></AppErrorBoundary>
              </div>
            : <AppErrorBoundary resetKey={location.pathname}><Outlet /></AppErrorBoundary>}
      </div>
      <DialogHost />
      {/* Anruf-Cockpit app-weit statt unter der Sales-CRM-Seite (UMBAU.md Phase 4).
          Es nimmt keine Props, liest alles aus dem Store und holt sein Skript selbst
          (CallCockpit.tsx: „unabhängig davon, WIE der Call gestartet wurde") — es hing nur
          zufällig unter SalesCrm. Hier gerendert, legt es sich über JEDE Seite, auch über die
          fraud0-Karte: der Wählklick verlässt den Stapel nicht mehr, also gibt es hinterher
          auch keine Position wiederherzustellen. Gilt für beide Shells (chromeless inbegriffen),
          weil `shellBody` beide trägt. */}
      {callViewOpen && <CallCockpit />}
      {/* Vor dem Anruf, nicht danach: DialGate steht VOR CallCockpit in der Kette (`openDialGate`
          → `resolveDialGate` → `startCall` → `callViewOpen`). Beide können nie gleichzeitig offen
          sein, weil startCall erst nach dem Schließen des Gates läuft. Punkt 52. */}
      <DialGate />
    </>
  );

  // Chromeless full-screen shell for the fraud0 review roles: no sidebar, no hamburger · only a slim
  // top bar (logo + user + logout). All SSE/branding/live-sync effects above run unchanged.
  if (isChromeless) {
    return (
      <div className={`flex flex-col h-[100dvh] fraud0-area bg-bg-primary${fraud0Light ? ' fraud0-light' : ''}`}>
        <header className="shrink-0 h-12 flex items-center justify-between px-4 border-b border-slate-800 bg-bg-surface">
          <div className="flex items-center gap-2 min-w-0">
            <img src="/leitkraft-logo.png" alt="Leitkraft" className="brand-logo-black h-6 w-6 object-contain" />
            <img src="/leitkraft-logo-white.png" alt="Leitkraft" className="brand-logo-white h-6 w-6 object-contain" />
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {/* Hell/Dunkel-Umschalter · chromeless Rollen haben keine Sidebar mit dem Toggle. */}
            <button
              onClick={toggleFraud0Theme}
              title={fraud0Dark ? 'Heller Modus' : 'Dunkler Modus'}
              className="p-1.5 rounded-lg text-text-muted hover:text-text-primary hover:bg-bg-elevated"
            >
              {fraud0Dark ? <Sun size={16} /> : <Moon size={16} />}
            </button>
            <UserAvatar
              displayName={user?.display_name}
              username={user?.username}
              avatarColor={user?.avatar_color}
              size="sm"
            />
            <button
              onClick={logout}
              title={t('nav.logout', lang)}
              className="p-1.5 rounded-lg text-text-muted hover:text-danger hover:bg-bg-elevated"
            >
              <LogOut size={16} />
            </button>
          </div>
        </header>
        <main className="flex-1 flex flex-col overflow-hidden">
          {shellBody}
        </main>
        <ToastContainer toasts={toasts} onRemove={removeToast} />
        {brandingModalOpen && (
          <BrandingSettingsModal onClose={() => setBrandingModalOpen(false)} />
        )}
      </div>
    );
  }

  return (
    <div className="app-shell fixed inset-0 flex bg-bg-primary">
      {/* Mobile menu button · auf der fokussierten fraud0-Leads-Ansicht ausgeblendet (clean, kein
          Chrome). Bei geöffneter Sidebar bleibt der Schließen-Button erreichbar. */}
      <button
        onClick={() => setSidebarOpen(!sidebarOpen)}
        className={`${onFraud0 && !sidebarOpen ? 'hidden' : 'lg:hidden'} fixed top-4 left-4 z-50 p-2 rounded-lg bg-bg-surface border border-slate-800 text-text-muted`}
      >
        {sidebarOpen ? <X size={20} /> : <Menu size={20} />}
      </button>

      {/* Desktop: Sidebar wieder einblenden, wenn eingeklappt */}
      {sidebarCollapsed && (
        <button
          onClick={() => setSidebarCollapsed(false)}
          className="hidden lg:flex fixed top-4 left-4 z-50 p-2 rounded-lg bg-bg-surface border border-slate-800 text-text-muted hover:text-text-main hover:bg-bg-elevated transition-colors"
          title={lang === 'de' ? 'Sidebar einblenden' : 'Show sidebar'}
        >
          <PanelLeft size={20} />
        </button>
      )}

      {/* Sidebar */}
      <aside
        className={`fixed lg:static inset-y-0 left-0 z-40 w-64 bg-bg-surface border-r border-slate-800 flex flex-col transition-transform lg:translate-x-0 ${
          (onFraud0 || onMintPage) ? 'fraud0-area' : 'fraud0-area bo-purple'
        } ${
          !fraud0Dark ? 'fraud0-light' : ''
        } ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        } ${sidebarCollapsed ? 'lg:hidden' : ''}`}
      >
        {/* Logo */}
        <div className="p-6 border-b border-slate-800 flex items-center gap-3">
          <img src="/leitkraft-logo.png" alt="Leitkraft" className="brand-logo-black h-[26px] w-[26px] object-contain" />
          <img src="/leitkraft-logo-white.png" alt="Leitkraft" className="brand-logo-white h-[26px] w-[26px] object-contain" />
          <span className="flex-1" />
          {/* Desktop-only: Sidebar einklappen */}
          <button
            onClick={() => setSidebarCollapsed(true)}
            className="hidden lg:flex p-1.5 rounded-lg text-text-muted hover:text-text-main hover:bg-bg-elevated transition-colors shrink-0"
            title={lang === 'de' ? 'Sidebar ausblenden' : 'Hide sidebar'}
          >
            <PanelLeftClose size={18} />
          </button>
        </div>

        {/* Nav */}
        <nav className="flex-1 p-4 space-y-4 overflow-y-auto">
          {visibleNavGroups.map((group, gi) => (
            <div key={gi}>
              {group.label && (
                <div className="px-3 mb-1 text-[10px] font-semibold uppercase tracking-wider text-text-muted/60">
                  {group.label}
                </div>
              )}
              <div className="space-y-1">
                {group.items.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.to === '/app'}
                    onClick={() => setSidebarOpen(false)}
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-colors ${
                        isActive
                          ? 'bg-accent/10 text-accent'
                          : 'text-text-muted hover:text-text-main hover:bg-bg-elevated'
                      }`
                    }
                  >
                    <item.icon size={18} />
                    {item.label}
                  </NavLink>
                ))}
              </div>
            </div>
          ))}
        </nav>

        {/* User */}
        <div className="p-4 border-t border-slate-800">
          <div className="flex items-center justify-between">
            <button
              onClick={() => { setSidebarOpen(false); navigate('/app/profile'); }}
              className="flex items-center gap-3 flex-1 min-w-0 hover:opacity-80 transition-opacity"
            >
              <UserAvatar
                displayName={user?.display_name}
                username={user?.username}
                avatarColor={user?.avatar_color}
                size="sm"
              />
              <div className="min-w-0 text-left">
                <div className="text-sm font-medium text-text-main truncate">
                  {user?.display_name || user?.username}
                </div>
                <div className="text-xs text-text-muted">{user?.role}</div>
              </div>
            </button>
            <div className="flex items-center gap-1 ml-2">
              <button
                onClick={toggleFraud0Theme}
                className="p-1.5 rounded-lg text-text-muted hover:text-text-main hover:bg-bg-elevated"
                title={fraud0Dark ? 'Heller Modus (Weiss)' : 'Dunkler Modus (Schwarz)'}
              >
                {fraud0Dark ? <Sun size={14} /> : <Moon size={14} />}
              </button>
              <button
                onClick={() => { setSidebarOpen(false); navigate('/app/profile'); }}
                className="p-1.5 rounded-lg text-text-muted hover:text-text-main hover:bg-bg-elevated"
                title="Profil"
              >
                <User size={14} />
              </button>
              {isAdmin && (
                <button
                  onClick={() => { setSidebarOpen(false); setBrandingModalOpen(true); }}
                  className="p-1.5 rounded-lg text-text-muted hover:text-text-main hover:bg-bg-elevated"
                  title={t('settings.branding_title', lang)}
                >
                  <Settings size={14} />
                </button>
              )}
              <button
                onClick={logout}
                className="p-1.5 rounded-lg text-text-muted hover:text-danger hover:bg-bg-elevated"
                title={t('nav.logout', lang)}
              >
                <LogOut size={14} />
              </button>
            </div>
          </div>
        </div>
      </aside>

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main content */}
      <main className="flex-1 flex flex-col overflow-hidden">
        {shellBody}
      </main>

      <ToastContainer toasts={toasts} onRemove={removeToast} />
      {brandingModalOpen && (
        <BrandingSettingsModal onClose={() => setBrandingModalOpen(false)} />
      )}
    </div>
  );
}
